import type { PortalInvoiceDetail } from '@erp/shared';
import { ArrowLeft, Download, FileText } from 'lucide-react';
import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Link, useParams } from 'react-router-dom';

import { Badge } from '../../components/shadcn/index.js';
import { Button, Card, Skeleton } from '../../components/ui/index.js';
import { customerApi } from '../../lib/customerApi.js';
import { formatMoney, trimMoney } from '../../lib/money.js';

import { downloadPortalPdf } from './download.js';

export function PortalInvoiceDetailPage(): JSX.Element {
  const { t, i18n } = useTranslation('portal');
  const { id = '' } = useParams();
  const [inv, setInv] = useState<PortalInvoiceDetail | null>(null);
  const [notFound, setNotFound] = useState(false);

  useEffect(() => {
    setInv(null);
    setNotFound(false);
    void customerApi()
      .get<PortalInvoiceDetail>(`/invoices/${id}`)
      .then((r) => setInv(r.data))
      .catch(() => setNotFound(true));
  }, [id]);

  const money = (v: string, c: string): string => formatMoney(v, { locale: i18n.language, currency: c });

  return (
    <div className="flex flex-col gap-4 pb-16 lg:pb-0">
      <Link to="/portal/statement?tab=invoices" className="inline-flex items-center gap-1 text-sm text-fg-muted hover:text-fg">
        <ArrowLeft className="h-4 w-4" aria-hidden />
        {t('invoices.detail.back')}
      </Link>

      {notFound ? (
        <Card className="p-6 text-sm text-danger">{t('loadError')}</Card>
      ) : !inv ? (
        <Card className="p-6"><Skeleton className="h-40 w-full" /></Card>
      ) : (
        <>
          <div className="flex flex-wrap items-center justify-between gap-3">
            <div className="flex items-center gap-3">
              <h1 className="text-2xl font-semibold">{t('invoices.detail.title', { number: inv.docNumber })}</h1>
              <Badge variant={inv.status === 'paid' ? 'success' : inv.status === 'cancelled' ? 'secondary' : 'warning'}>
                {t(`status.${inv.status}`)}
              </Badge>
            </div>
            <div className="flex flex-wrap items-center gap-2">
              {/* The same printable paper staff see — minted customer-side by
                  the portal, ownership re-checked on the server. */}
              <Button
                variant="secondary"
                onClick={() => {
                  void customerApi()
                    .get<{ url: string }>(`/invoices/${inv.id}/paper`)
                    .then((r) => window.open(r.data.url, '_blank', 'noopener'))
                    .catch(() => undefined);
                }}
              >
                <FileText className="me-1.5 h-4 w-4" aria-hidden />
                {t('paper.viewInvoice', { defaultValue: 'View invoice' })}
              </Button>
              <Button variant="secondary" onClick={() => void downloadPortalPdf(`/invoices/${inv.id}/pdf`, `${inv.docNumber}.pdf`, t('loadError'))}>
                <Download className="me-1.5 h-4 w-4" aria-hidden />
                {t('invoices.download')}
              </Button>
            </div>
          </div>

          <div className="grid grid-cols-2 gap-3 text-sm sm:max-w-md">
            <div className="text-fg-muted">{t('columns.date')}</div>
            <div>{inv.docDate}</div>
            <div className="text-fg-muted">{t('columns.due')}</div>
            <div>{inv.dueDate ?? '—'}</div>
          </div>

          <Card className="overflow-hidden p-0">
            <table className="w-full text-sm">
              <thead className="bg-bg-subtle text-fg-muted">
                <tr>
                  <th className="px-4 py-2 text-start font-semibold">{t('invoices.detail.description')}</th>
                  <th className="px-4 py-2 text-end font-semibold">{t('invoices.detail.qty')}</th>
                  <th className="px-4 py-2 text-end font-semibold">{t('invoices.detail.unitPrice')}</th>
                  <th className="px-4 py-2 text-end font-semibold">{t('invoices.detail.lineTotal')}</th>
                </tr>
              </thead>
              <tbody>
                {inv.lines.map((l) => (
                  <tr key={l.lineNo} className="border-t border-border">
                    <td className="px-4 py-2">{l.description ?? '—'}</td>
                    <td className="px-4 py-2 text-end font-mono tabular-nums">{trimMoney(l.quantity)}</td>
                    <td className="px-4 py-2 text-end font-mono tabular-nums">{money(l.unitPrice, inv.currencyCode)}</td>
                    <td className="px-4 py-2 text-end font-mono tabular-nums">{money(l.lineTotal, inv.currencyCode)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </Card>

          <div className="ms-auto w-full max-w-xs space-y-1 text-sm">
            <div className="flex justify-between"><span className="text-fg-muted">{t('invoices.detail.subtotal')}</span><span className="font-mono tabular-nums">{money(inv.subtotal, inv.currencyCode)}</span></div>
            {Number(inv.taxTotal) > 0 && <div className="flex justify-between"><span className="text-fg-muted">{t('invoices.detail.tax')}</span><span className="font-mono tabular-nums">{money(inv.taxTotal, inv.currencyCode)}</span></div>}
            <div className="flex justify-between font-semibold"><span>{t('invoices.detail.total')}</span><span className="font-mono tabular-nums">{money(inv.total, inv.currencyCode)}</span></div>
            <div className="flex justify-between"><span className="text-fg-muted">{t('invoices.detail.paid')}</span><span className="font-mono tabular-nums">{money(inv.amountPaid, inv.currencyCode)}</span></div>
            <div className="flex justify-between border-t border-border pt-1 font-semibold"><span>{t('invoices.detail.balance')}</span><span className="font-mono tabular-nums">{money(inv.balance, inv.currencyCode)}</span></div>
          </div>

          {inv.notes && (
            <Card className="p-4 text-sm">
              <div className="mb-1 text-xs font-semibold uppercase tracking-wide text-fg-muted">{t('invoices.detail.notes')}</div>
              {inv.notes}
            </Card>
          )}
        </>
      )}
    </div>
  );
}
